// src/pages/business/bookings/BookingsPage.tsx
// Mimics the mobile BookingsScreen with enhanced web functionality

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar,
  Clock,
  User,
  Phone,
  CheckCircle,
  XCircle,
  RefreshCw,
  Loader2,
  Filter,
  Search,
  ChevronDown,
} from 'lucide-react';
import apiClient from '@/services/apiClient';
import { useToast } from '@/contexts/ToastContext';

interface Booking {
  id: string;
  service_name: string;
  customer_name: string;
  customer_phone: string;
  booking_date: string;
  booking_time: string;
  status: 'pending' | 'confirmed' | 'completed' | 'cancelled';
  notes?: string;
  created_at: string;
}

const statusConfig = {
  pending: { label: 'Pending', color: '#F59E0B', bgColor: 'rgba(245,158,11,0.12)' },
  confirmed: { label: 'Confirmed', color: '#3B82F6', bgColor: 'rgba(59,130,246,0.12)' },
  completed: { label: 'Completed', color: '#10B981', bgColor: 'rgba(16,185,129,0.12)' },
  cancelled: { label: 'Cancelled', color: '#EF4444', bgColor: 'rgba(239,68,68,0.12)' },
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatTime = (timeString: string) => {
  return timeString;
};

// ── Booking Card Component ─────────────────────────────────────────────────────
const BookingCard: React.FC<{
  booking: Booking;
  onUpdateStatus: (id: string, status: Booking['status']) => void;
}> = ({ booking, onUpdateStatus }) => {
  const status = statusConfig[booking.status];

  return (
    <div className="bg-light-card dark:bg-dark-card rounded-xl p-4 mb-3 border border-light-border dark:border-dark-border shadow-sm">
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <p className="font-bold text-light-text dark:text-dark-text" style={{ fontSize: 16 }}>
            {booking.service_name}
          </p>
          <p className="text-sm text-light-subtle dark:text-dark-subtle mt-1">
            {booking.customer_name}
          </p>
        </div>
        <span
          className="px-3 py-1 rounded-full text-xs font-semibold"
          style={{ backgroundColor: status.bgColor, color: status.color }}
        >
          {status.label}
        </span>
      </div>

      {/* Details */}
      <div className="flex items-center gap-4 mb-3">
        <div className="flex items-center gap-2">
          <Calendar size={16} className="text-light-subtle dark:text-dark-subtle" />
          <span className="text-sm text-light-text dark:text-dark-text">{formatDate(booking.booking_date)}</span>
        </div>
        <div className="flex items-center gap-2">
          <Clock size={16} className="text-light-subtle dark:text-dark-subtle" />
          <span className="text-sm text-light-text dark:text-dark-text">{formatTime(booking.booking_time)}</span>
        </div>
      </div>

      {booking.customer_phone && (
        <div className="flex items-center gap-2 mb-3">
          <Phone size={16} className="text-light-subtle dark:text-dark-subtle" />
          <span className="text-sm text-light-text dark:text-dark-text">{booking.customer_phone}</span>
        </div>
      )}

      {booking.notes && (
        <div className="mb-3 p-3 bg-light-bg dark:bg-dark-bg rounded-lg">
          <p className="text-sm text-light-text dark:text-dark-text">{booking.notes}</p>
        </div>
      )}

      {/* Actions */}
      {booking.status === 'pending' && (
        <div className="flex gap-2 mt-3 pt-3 border-t border-light-border dark:border-dark-border">
          <button
            onClick={() => onUpdateStatus(booking.id, 'confirmed')}
            className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-semibold text-white"
            style={{ backgroundColor: '#3B82F6' }}
          >
            <CheckCircle size={16} />
            Confirm
          </button>
          <button
            onClick={() => onUpdateStatus(booking.id, 'cancelled')}
            className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-semibold text-white"
            style={{ backgroundColor: '#EF4444' }}
          >
            <XCircle size={16} />
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

// ── Main Page Component ────────────────────────────────────────────────────────
const BookingsPage: React.FC = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const navigate = useNavigate();
  const { error: toastError } = useToast();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [filter, setFilter] = useState<'all' | Booking['status']>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get(`/businesses/${businessId}/bookings`);
      setBookings(res.data.bookings || []);
    } catch (error: any) {
      console.error('Failed to fetch bookings:', error);
      toastError('Failed to load bookings');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, [businessId]);

  const onRefresh = () => {
    setRefreshing(true);
    fetchBookings();
  };

  const handleUpdateStatus = async (bookingId: string, newStatus: Booking['status']) => {
    try {
      await apiClient.patch(`/businesses/${businessId}/bookings/${bookingId}/status`, {
        status: newStatus,
      });
      fetchBookings();
    } catch (error: any) {
      console.error('Failed to update booking status:', error);
      toastError('Failed to update booking');
    }
  };

  const filteredBookings = useMemo(() => {
    let result = bookings;

    // Apply status filter
    if (filter !== 'all') {
      result = result.filter((b) => b.status === filter);
    }

    // Apply search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (b) =>
          b.service_name.toLowerCase().includes(query) ||
          b.customer_name.toLowerCase().includes(query)
      );
    }

    return result;
  }, [bookings, filter, searchQuery]);

  // Group bookings by date
  const groupedBookings = useMemo(() => {
    const groups: Record<string, Booking[]> = {};

    filteredBookings.forEach((booking) => {
      const date = booking.booking_date;
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(booking);
    });

    return Object.entries(groups).map(([date, data]) => ({
      date,
      data,
    }));
  }, [filteredBookings]);

  if (loading) {
    return (
      <div className="min-h-screen bg-light-bg dark:bg-dark-bg">
        <div className="sticky top-0 z-40 bg-light-card dark:bg-dark-card border-b border-light-border dark:border-dark-border px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(`/business/${businessId}`)} className="flex-shrink-0">
            <ArrowLeft size={24} className="text-primary" />
          </button>
          <h1 className="flex-1 font-bold text-light-text dark:text-dark-text" style={{ fontSize: 20 }}>
            Service Bookings
          </h1>
        </div>
        <div className="flex items-center justify-center py-24">
          <div className="text-center">
            <Loader2 size={40} className="animate-spin mx-auto text-primary" />
            <p className="mt-4 text-light-subtle dark:text-dark-subtle">Loading bookings...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-light-bg dark:bg-dark-bg pb-24">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-light-card dark:bg-dark-card border-b border-light-border dark:border-dark-border px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate(`/business/${businessId}`)} className="flex-shrink-0">
          <ArrowLeft size={24} className="text-primary" />
        </button>
        <h1 className="flex-1 font-bold text-light-text dark:text-dark-text" style={{ fontSize: 20 }}>
          Service Bookings
        </h1>
        <button
          onClick={() => navigate(`/business/${businessId}/orders`)}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold"
          style={{ backgroundColor: 'rgba(79,70,229,0.12)', color: '#4F46E5' }}
        >
          Orders
        </button>
      </div>

      <div className="max-w-5xl mx-auto px-4 pt-4">
        {/* Search and Filter */}
        <div className="flex gap-3 mb-4">
          <div className="flex-1 relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-light-subtle dark:text-dark-subtle" />
            <input
              type="text"
              placeholder="Search bookings..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 rounded-xl bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border text-sm text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>
          <div className="relative">
            <button
              onClick={() => setFilter(filter === 'all' ? 'pending' : filter === 'pending' ? 'confirmed' : filter === 'confirmed' ? 'completed' : filter === 'completed' ? 'cancelled' : 'all')}
              className="flex items-center gap-2 px-4 py-3 rounded-xl bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border text-sm font-semibold text-light-text dark:text-dark-text"
            >
              <Filter size={16} />
              {filter === 'all' ? 'All' : statusConfig[filter].label}
              <ChevronDown size={16} />
            </button>
          </div>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-4 gap-3 mb-5">
          <div className="bg-light-card dark:bg-dark-card rounded-xl p-3 text-center">
            <p className="text-2xl font-bold text-light-text dark:text-dark-text">{bookings.length}</p>
            <p className="text-xs text-light-subtle dark:text-dark-subtle">Total</p>
          </div>
          <div className="bg-light-card dark:bg-dark-card rounded-xl p-3 text-center">
            <p className="text-2xl font-bold" style={{ color: '#F59E0B' }}>
              {bookings.filter((b) => b.status === 'pending').length}
            </p>
            <p className="text-xs text-light-subtle dark:text-dark-subtle">Pending</p>
          </div>
          <div className="bg-light-card dark:bg-dark-card rounded-xl p-3 text-center">
            <p className="text-2xl font-bold" style={{ color: '#3B82F6' }}>
              {bookings.filter((b) => b.status === 'confirmed').length}
            </p>
            <p className="text-xs text-light-subtle dark:text-dark-subtle">Confirmed</p>
          </div>
          <div className="bg-light-card dark:bg-dark-card rounded-xl p-3 text-center">
            <p className="text-2xl font-bold" style={{ color: '#10B981' }}>
              {bookings.filter((b) => b.status === 'completed').length}
            </p>
            <p className="text-xs text-light-subtle dark:text-dark-subtle">Completed</p>
          </div>
        </div>

        {/* Bookings List */}
        {groupedBookings.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center gap-4">
            <Calendar size={64} className="text-light-subtle dark:text-dark-subtle" />
            <p className="font-semibold text-light-text dark:text-dark-text" style={{ fontSize: 18 }}>
              No Bookings Found
            </p>
            <p className="text-sm text-light-subtle dark:text-dark-subtle">
              {searchQuery || filter !== 'all'
                ? 'Try adjusting your search or filter'
                : 'Service bookings will appear here'}
            </p>
          </div>
        ) : (
          <div>
            {groupedBookings.map((group) => (
              <div key={group.date} className="mb-6">
                <p className="font-semibold text-light-text dark:text-dark-text mb-3" style={{ fontSize: 15 }}>
                  {formatDate(group.date)}
                </p>
                {group.data.map((booking) => (
                  <BookingCard
                    key={booking.id}
                    booking={booking}
                    onUpdateStatus={handleUpdateStatus}
                  />
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Refresh Button - Fixed at bottom */}
      <button
        onClick={onRefresh}
        disabled={refreshing}
        className="fixed bottom-6 right-6 w-14 h-14 rounded-full flex items-center justify-center shadow-lg z-50"
        style={{ backgroundColor: '#4F46E5' }}
      >
        <RefreshCw size={24} className={`text-white ${refreshing ? 'animate-spin' : ''}`} />
      </button>
    </div>
  );
};

export default BookingsPage;